/** Google Analytics 4（本番ホストのみ index.html で gtag 読込） */

export const GA_MEASUREMENT_ID = 'G-PX24ZC2WG4';

const SUPPORTED_LOCALES = ['ja', 'en', 'zh-CN', 'fr', 'es', 'de', 'pt', 'ru'] as const;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

function gtagSafe(...args: unknown[]): void {
  if (typeof window.gtag === 'function') {
    window.gtag(...args);
  }
}

function normalizePathname(pathname: string): string {
  const trimmed = pathname.trim().split('?')[0].replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

/** GA4 レポート用のアプリ識別子（feature カード ID と整合） */
export function resolveGaAppSurface(pathname: string): string {
  const path = normalizePathname(pathname);
  const segments = path.split('/').filter(Boolean);
  let i = 0;
  if (segments[0] && (SUPPORTED_LOCALES as readonly string[]).includes(segments[0])) {
    i = 1;
  }
  const first = segments[i] ?? '';
  const second = segments[i + 1] ?? '';
  const third = segments[i + 2] ?? '';

  if (!first) return 'home';

  if (first === 'login' || first === 'register' || first === 'password-reset') return 'auth';
  if (first === 'oauth') return 'oauth';
  if (first === 'dashboard') return 'dashboard';
  if (first === 'billing') return 'billing';
  if (first === 'profile') return 'profile';
  if (first === 'help') return 'help';
  if (first === 'contact') return 'contact';
  if (first === 'privacy' || first === 'terms') return 'legal';
  if (first === 'usage-record') return 'usage_record';
  if (first === 'feature-requests') return 'feature_requests';
  if (first === 'keisuke') return 'keisuke';
  if (first === 'gaisuke') {
    if (second === 'settings') return 'gaisuke_admin';
    if (second === 'weekly') return 'gaisuke';
    if (second === 'faq') return 'gaisuke';
    return 'gaisuke';
  }
  if (first === 'arena' && second === 'keiba') return 'keiba_arena';
  if (first === 'arena' && second === 'banquet') return 'banquet';
  if (first === 'arena' && second === 'econ') return 'econ_report';
  if (first === 'arena' && second === 'fx') return 'gaisuke_fx';
  if (first === 'keiba-arena') return 'keiba_arena';
  if (first === 'labo-suke') return 'labo_suke';
  if (first === 'transit-pass-pool') return 'transit_pass';
  if (first === 'gene-analysis') return 'gene_analysis';
  if (first === 'reservations') {
    if (second === 'share') return 'reservation';
    return 'reservation';
  }
  if (first === 'samples') {
    if (second === 'encrypted' || second === 'share') return 'sample_public_share';
    return 'sample';
  }
  if (first === 'storage-rooms') return 'sample';
  if (first === 'excel-import' || first === 'excel-export' || first === 'reports') return 'sample';
  if (first === 'users') return 'admin_users';
  if (first === 'admin') {
    if (!second) return 'admin';
    if (second === 'insights') return 'admin_insights';
    if (second === 'access-logs') return 'admin_access_logs';
    if (second === 'conversion') return 'admin_conversion';
    if (second === 'user-activity') return 'admin_user_activity';
    if (second === 'database-backup') return 'admin_database';
    return `admin_${second}`;
  }
  if (first === 'access-logs') return 'admin_access_logs';
  if (first === 'conversion-funnel') return 'admin_conversion';
  if (first === 'user-activity-logs') return 'admin_user_activity';
  if (first === 'database-backup') return 'admin_database';
  if (first === '500') return 'error';

  return first;
}

/** パス内のサブセクション（計助 formulas 等） */
export function resolveGaAppSection(pathname: string): string {
  const path = normalizePathname(pathname);
  const segments = path.split('/').filter(Boolean);
  let i = 0;
  if (segments[0] && (SUPPORTED_LOCALES as readonly string[]).includes(segments[0])) {
    i = 1;
  }
  const rest = segments.slice(i);
  if (rest.length <= 1) return '';
  return rest.slice(1).join('/');
}

function hasAuthToken(): boolean {
  try {
    return Boolean(localStorage.getItem('token'));
  } catch {
    return false;
  }
}

/** SPA 遷移ごとの page_view（GA4 Pages & screens / Intelligent Home） */
export function trackGaPageView(pathname: string, options?: { loggedIn?: boolean }): void {
  const pagePath = normalizePathname(pathname);
  const search = window.location.search || '';
  const pagePathWithQuery = pagePath + search;
  const pageLocation = window.location.href;
  const pageTitle = document.title || 'kensuke-system';
  const loggedIn = options?.loggedIn ?? hasAuthToken();
  const appSurface = resolveGaAppSurface(pagePath);
  const appSection = resolveGaAppSection(pagePath);

  gtagSafe('set', 'user_properties', {
    login_state: loggedIn ? 'logged_in' : 'anonymous',
  });

  // セッション文脈を更新（send_page_view: false のため二重計測にならない）
  gtagSafe('config', GA_MEASUREMENT_ID, {
    page_path: pagePathWithQuery,
    page_title: pageTitle,
    page_location: pageLocation,
  });

  gtagSafe('event', 'page_view', {
    send_to: GA_MEASUREMENT_ID,
    page_path: pagePathWithQuery,
    page_location: pageLocation,
    page_title: pageTitle,
    login_state: loggedIn ? 'logged_in' : 'anonymous',
    app_surface: appSurface,
    app_section: appSection || undefined,
  });
}

/** カスタムイベント（ツール入口など） */
export function trackGaEvent(
  eventName: string,
  params?: Record<string, string | number | boolean | undefined>
): void {
  const cleaned: Record<string, string | number | boolean> = {};
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) cleaned[k] = v;
    }
  }
  gtagSafe('event', eventName, { send_to: GA_MEASUREMENT_ID, ...cleaned });
}
